import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { AnimatedLoader, CustomTooltip, IOptionType, NavBar, NavDrawer, SideMenuItem, lowercase } from '@zz2/zz2-ui';
import lodash from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { IUser } from '../../@types/model/right/user/user';
import { useAppDispatch, useAppSelector } from '../../@types/redux';
import { ICONS_LOCATION, RIGHT_GRANTS } from '../../appConstants';
import { useGetRights } from '../../hooks/query/right/rightQueries';
import EnvHelper from '../../service/helper/envHelper';
import UserHelper from '../../service/helper/userHelper';
import * as localStorageService from '../../service/localStorageService';
import AuthThunks from '../../store/auth/thunk';
import FreshServiceActions from '../../store/freshService/actions';
import GeneralActions from '../../store/general/actions';
import { VERSION } from '../../version';
import FreshServiceTicketCreationDialog from '../freshService/FreshServiceTicketCreationDialog';
import Routes from './Routes';
import UserSettings from './UserSettings';
import { IRight } from '../../@types/model/right/right/right';
import { OptionType } from '../../@types/model/optionType';
import { ISite } from '../../@types/model/masterData/site/site';

const NavFrame = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const currentUser = useAppSelector<IUser | null>(x => x.auth.session?.user ?? null);
    const isNavDrawerOpen = useAppSelector(x => x.general.isNavDrawerOpen);
    const freshTicketServiceDescription = useAppSelector(x => x.freshService.supportTicketDescription);
    const isFreshServiceTicketDialogOpen = useAppSelector(x => x.freshService.isSupportTicketDialogOpen);

    const userSelectedSites = localStorageService.getUserSelectedSitesLocalStorage();
    const [isUserSettingsDialogOpen, setIsUserSettingsDialogOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                              Queries
     * ==============================================================================================================*/

    const { isLoading: isLoadingRights, data: rights } = useGetRights();

    /*================================================================================================================
     *                                              Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (userSelectedSites.length === 0) {
            setIsUserSettingsDialogOpen(true);
        }
    }, [userSelectedSites]);
    
    useEffect(() => {
        if (!currentUser?.userSites) return;
    
        if (currentUser.userSites.length > 0) {
            const selectedSites : Array<IOptionType> = currentUser.userSites
                .filter(x =>
                    x.isActive
                    && !!x.site
                    && !!x.site.isActive
                    && userSelectedSites.map(s => s.value).includes(x.siteId))
                .map((x) => OptionType.fromDataModel(x.site as ISite));
            
            localStorageService.setUserSelectedSitesLocalStorage(selectedSites);
        }
    }, [currentUser?.userSites]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const signOut = () : void => {
        dispatch(GeneralActions.setNavDrawer(false));
        dispatch(AuthThunks.logout());
    };

    const openUserSettings = () : void => {
        setIsUserSettingsDialogOpen(true);
    };

    const closeUserSettings = () : void => {
        setIsUserSettingsDialogOpen(false);
    };

    const openDrawer = () : void => {
        dispatch(GeneralActions.setNavDrawer(true));
    };

    const closeDrawer = () : void => {
        dispatch(GeneralActions.setNavDrawer(false));
    };

    const openSupportTicketDialog = () : void => {
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(true));
    };

    const closeFreshServiceTicketDialog = () : void => {
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(false));
    };

    const requestAccess = (right : IRight) : void => {
        dispatch(FreshServiceActions.setSupportTicketDescription(`Requesting permission: ${right.name} - View.`));
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(true));
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const siteHeading = useMemo<string>(() => {
        if (!currentUser?.userSites) return '';
        return UserHelper.getUserSiteHeading(currentUser, userSelectedSites);
    }, [userSelectedSites, currentUser, isUserSettingsDialogOpen]);

    const currentUserRights = useMemo(() => {
        return currentUser?.userRights.map(x => x.right);
    }, []);

    const userCurrentRight = useMemo<IRight | undefined>(() => {
        const urlSections = location.pathname.split('/'); 
        const firstPartition = urlSections.slice(0, -1).join('/');
        
        return currentUserRights?.filter(x => x.isActive)?.find(
            x => x.url === (urlSections[urlSections.length - 1].includes('Id') 
                ? firstPartition
                : location.pathname
            )
        );

    }, [currentUser, location.pathname]);


    const breadcrumbs = useMemo<Array<{ name : string; url : string }>>(() => {
        const breadcrumbList : Array<{ name : string; url : string }> = [];

        if (userCurrentRight) {
            let currentRight : IRight | undefined = userCurrentRight;

            do {
                breadcrumbList.push({ name: currentRight.name, url: currentRight.url ?? '' });
                currentRight = currentUserRights?.find(x => x.id === currentRight?.breadcrumbParentId);
            } while (currentRight);
        }
        return breadcrumbList.reverse();
    }, [currentUser, location, currentUserRights, userCurrentRight]);

    const userSettings = useMemo<React.ReactElement>(() => {
        return <UserSettings 
            isOpen={isUserSettingsDialogOpen}
            currentUser={currentUser}
            setIsUserDialogOpenCallback={(state : boolean) : void => setIsUserSettingsDialogOpen(state)}
            onClose={closeUserSettings}
            signOut={signOut}
        />;
    }, [isUserSettingsDialogOpen, currentUser]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    const getIcons = (name : string) : React.ReactElement => {
        return (<img className={'h30 w30'} src={`${ICONS_LOCATION}/${lowercase(name).replace(/ /g, '_')}.svg`}/>);
    };
    const isParentOfCurrentPage = (parentId : number, rights ?: Array<IRight>, pathname ?: string) : boolean => {
        const currentPageRight = rights?.find(x => x.url === pathname);
        const parentRight = rights?.find(x => x.id === parentId);

        if (currentPageRight?.parentId) {
            return (currentPageRight.parentId === parentId) || (!!parentRight?.parentId && isParentOfCurrentPage(parentRight.parentId, rights, pathname));
        }
        return false;
    };

    const renderListSection = (right : IRight, children : Array<IRight>, rights ?: Array<IRight>, pathname ?: string) : JSX.Element => {
        const userRights = currentUser?.userRights;
        const hasViewRight = userRights?.some(x =>
            x.isActive
            && x.right.id === right.id
            && x.rightGrantLevel === RIGHT_GRANTS.View);

        return (
            <CustomTooltip
                title={!hasViewRight ? `Right: ${right.name}, level: View is required for access. \n\nClick to request permission.` : ''}
                onClick={() : void => requestAccess(right)}
                rightValidation={!hasViewRight}>
                <SideMenuItem
                    className={'pt10'}
                    paddingLeft={0}
                    icon={getIcons(lowercase(right.name))}
                    title={right.name}
                    key={'section_' + right.id}
                    boldTitle={isParentOfCurrentPage(right.id, rights, pathname)}
                    disabled={!hasViewRight}>
                    {children.map(x => renderListItem(x, false, pathname))}
                </SideMenuItem>
            </CustomTooltip>
        );
    };

    const renderListItem = (right : IRight, hasIcon : boolean, pathname ?: string) : JSX.Element => {
        const userRights = currentUser?.userRights;
        const hasViewRight = userRights?.some(x =>
            x.isActive
            && x.right.id === right.id
            && x.rightGrantLevel === RIGHT_GRANTS.View);

        return (
            <CustomTooltip
                title={!hasViewRight ? `Right: ${right.name}, level: View is required for access. \n\nClick to request permission.` : ''}
                onClick={() : void => requestAccess(right)}
                rightValidation={!hasViewRight}>
                <Link key={`link ${right.url}`} to={right.url ?? ''} onClick={closeDrawer} style={{ pointerEvents: hasViewRight ? 'auto' : 'none', textDecoration: 'unset', color: 'unset' }} className={'tdus cb'}>
                    <ListItemButton 
                        key={right.guid} 
                        disabled={!hasViewRight}>
                        {
                            hasIcon ?
                                <ListItemIcon>
                                    {<img className={'h30 w30 mt8 mb8'} src={`${ASSET_BASE}/assets/icons/${lowercase(right.name).replace(/ /g, '_')}.svg`} />}
                                </ListItemIcon>
                                :
                                <div className={'h30 w30'} />
                        }
                        <ListItemText disableTypography className={`${pathname === right.url ? 'fw550' : ''} pl0`} inset primary={right.name} />
                    </ListItemButton>
                </Link>
            </CustomTooltip>
        );
    };

    const sections = useMemo<Array<JSX.Element>>(() => {
        if (isLoadingRights) {
            return [<AnimatedLoader key='loader'/>];
        }        

        return lodash.chain(rights)
            .filter(x => x.isOnNavDrawer && x.isActive && !x.parentId)
            .filter(x => !!x.sectionOrder)
            .sortBy(x => x.sectionOrder)
            .map((x) => {
                const userRights = currentUser?.userRights;
                const hasParentViewRight = userRights?.some(userRight =>
                    userRight.isActive
                    && userRight.right.id === x.id
                    && userRight.rightGrantLevel === RIGHT_GRANTS.View) ?? false;

                return {
                    hasParentViewRight,
                    section: x,
                    children: lodash.chain(rights)
                        .filter(y => y.isOnNavDrawer && y.isActive && y.parentId === x.id)
                        .filter(y => !!y.pageOrder)
                        .sortBy(y => y.pageOrder)
                        .map((child) => {
                            const hasChildViewRight = userRights?.some(userRight =>
                                userRight.isActive
                                && userRight.right.id === x.id
                                && userRight.rightGrantLevel === RIGHT_GRANTS.View) ?? false;

                            return {
                                hasChildViewRight,
                                child,
                            };
                        })
                        .orderBy(x => x.hasChildViewRight, 'desc')
                        .value(),
                };
            })
            .orderBy(x => x.hasParentViewRight, 'desc')
            .map((x) => {
                return x.section.isPage
                    ? renderListItem(x.section, true, location.pathname)
                    : renderListSection(x.section, x.children.map(x => x.child), currentUserRights, location.pathname);
            }).value();

    }, [currentUser, rights, location.pathname, isLoadingRights]);

    return ( 
        <div className={'fdc hfill'}>
            <NavBar
                env={EnvHelper.getEnvName()}
                version={VERSION.version}
                currentUser={currentUser}
                signOut={signOut}
                breadcrumbs={breadcrumbs}
                openDrawer={openDrawer}
                closeDrawer={closeDrawer}
                openUserSettings={openUserSettings}
                closeUserSettings={closeUserSettings}
                isLatestVersion  
                headings={[siteHeading]}
                userSettingsDialog={userSettings}             
            />
            <div className={'fdr flx1 oyh'}>
                <NavDrawer
                    env={EnvHelper.getEnvName()} 
                    path={location.pathname}
                    currentUser={currentUser}
                    isOpen={isNavDrawerOpen}
                    isLoading={false}
                    sections={sections}
                    logOut={signOut}
                    closeDrawer={closeDrawer}
                    onHelpClick={openSupportTicketDialog}
                />
                <Routes />
            </div>
            <FreshServiceTicketCreationDialog 
                isOpen={isFreshServiceTicketDialogOpen}
                onDismiss={closeFreshServiceTicketDialog}
                description={freshTicketServiceDescription}
            />
        </div>
    );
};

export default NavFrame;